import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { motionMs } from "./tokens";

type Props = {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  id?: string;
  "data-testid"?: string;
  /** Mount children on first open and keep them, so a lazy query fires once. */
  lazy?: boolean;
};

/**
 * Height and opacity both ways; children stay mounted, inert while shut.
 * Clips only while shut or turning: an open clip cuts FLIP glides from below a panel's new height.
 */
const Collapse: React.FC<Props> = ({
  open,
  children,
  className,
  lazy = false,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  const [shown, setShown] = useState(open);
  const [turning, setTurning] = useState(false);
  if (shown !== open) {
    setShown(open);
    setTurning(motionMs("base") > 0);
  }
  useEffect(() => {
    if (!turning) return undefined;
    const t = window.setTimeout(() => setTurning(false), motionMs("base"));
    return () => window.clearTimeout(t);
  }, [open, turning]);
  useEffect(() => {
    ref.current?.toggleAttribute("inert", !open);
  }, [open]);
  return (
    <div
      {...rest}
      ref={ref}
      aria-hidden={open ? undefined : true}
      className={cn("motion-collapse", className)}
      style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
    >
      {/* min-w-0 keeps the track width the clip used to give. */}
      <div
        className={cn(
          "min-h-0 min-w-0",
          (!open || turning) && "overflow-hidden",
        )}
      >
        {(!lazy || opened) && children}
      </div>
    </div>
  );
};

export default Collapse;
